import React, {useEffect, useRef, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import styled, {keyframes} from "styled-components";

// How long the collapsed pill sits before it slides open, in ms.
const EXPAND_DELAY = 650;

const FALLBACK_AVATAR = "https://hackernoon.imgix.net/hn-icon.png";

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Toast = styled.div`
  position: fixed;
  right: 20px;
  bottom: 24px;
  z-index: 100000;
  display: flex;
  align-items: center;
  gap: ${({$expanded: expanded}) => (expanded ? "10px" : "0px")};
  width: ${({$expanded: expanded}) => (expanded ? "420px" : "56px")};
  max-width: calc(100vw - 40px);
  height: 56px;
  padding: 6px;
  overflow: hidden;
  cursor: pointer;
  box-sizing: border-box;

  background: ${({theme}) => theme.almostBlack};
  border: 2px solid ${({theme}) => theme.mainColor};
  color: #ffffff;
  font-family: ${({theme}) => theme.monoFont};
  box-shadow: 4px 4px 0px rgba(0, 0, 0, 0.35);

  animation: ${slideUp} 0.25s ease-out;
  transition: width 0.4s ease-out, gap 0.4s ease-out, opacity 0.2s ease-out, transform 0.2s ease-out;

  ${({$leaving: leaving}) =>
    leaving &&
    `
    opacity: 0;
    transform: translateY(12px);
  `}

  &:hover {
    border-color: ${({theme}) => theme.newGreen};
  }

  @media screen and (max-width: 700px) {
    right: 12px;
    left: 12px;
    bottom: 12px;
    width: auto;
    max-width: none;
  }
`;

const Avatar = styled.img`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  object-fit: cover;
  border: 2px solid ${({theme}) => theme.mainColor};
  background: #ffffff;
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  opacity: ${({$expanded: expanded}) => (expanded ? 1 : 0)};
  transition: opacity 0.2s ease-out ${({$expanded: expanded}) => (expanded ? "0.2s" : "0s")};
`;

const Text = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.3;
  color: #ffffff;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: ${({$expanded: expanded}) => (expanded ? 1 : 0)};
  pointer-events: ${({$expanded: expanded}) => (expanded ? "auto" : "none")};
  transition: opacity 0.2s ease-out ${({$expanded: expanded}) => (expanded ? "0.2s" : "0s")};
`;

const ViewAll = styled.button`
  height: 28px;
  padding: 0 8px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  background: ${({theme}) => theme.mainColor};
  color: ${({theme}) => theme.mainTextColor};
  font-family: ${({theme}) => theme.monoFont};
  font-size: 11px;
  font-weight: bold;

  &:hover {
    background: ${({theme}) => theme.newGreen};
  }
`;

const Dismiss = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  cursor: pointer;
  background: transparent;
  color: #ffffff;
  font-family: ${({theme}) => theme.monoFont};
  font-size: 16px;
  line-height: 1;

  &:hover {
    background: ${({theme}) => theme.veryDarkGrey};
  }
`;

const SrOnly = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * Toast for the newest unread notification. Starts as a collapsed pill and
 * slides open after EXPAND_DELAY. Clicking it marks the notification read and
 * opens its link; "ALL" goes to the dashboard without marking anything read.
 */
const NotificationPopup = ({noti, unreadCount, onDismiss, onRead}) => {
  const history = useHistory();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const expandTimer = useRef(null);
  const leaveTimer = useRef(null);

  const id = noti?._id || noti?.id;

  useEffect(() => {
    // Re-run per notification so a toast replacing another one animates too.
    setExpanded(false);
    setLeaving(false);
    clearTimeout(expandTimer.current);
    expandTimer.current = setTimeout(() => setExpanded(true), EXPAND_DELAY);
    return () => clearTimeout(expandTimer.current);
  }, [id]);

  useEffect(
    () => () => {
      clearTimeout(expandTimer.current);
      clearTimeout(leaveTimer.current);
    },
    [],
  );

  if (!noti || location?.pathname?.startsWith("/notifications")) return null;

  // Let the exit transition play out before the toast leaves the tree.
  const close = after => {
    setLeaving(true);
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => {
      onDismiss?.();
      after?.();
    }, 200);
  };

  const handleOpen = () => {
    onRead?.(noti);
    close(() => {
      if (noti?.link) window.open(noti.link, "_blank", "noopener,noreferrer");
    });
  };

  const handleViewAll = e => {
    e.stopPropagation();
    close(() => history.push("/notifications"));
  };

  const handleDismiss = e => {
    e.stopPropagation();
    close();
  };

  const text = noti?.text || "";
  const badge = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <>
      <SrOnly aria-live="polite" aria-atomic="true">
        {`New notification: ${text}${unreadCount > 1 ? `. ${unreadCount} unread.` : ""}`}
      </SrOnly>

      <Toast
        role="button"
        tabIndex={0}
        $expanded={expanded}
        $leaving={leaving}
        onClick={handleOpen}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpen();
          }
        }}
      >
        <Avatar src={noti?.who?.avatar || noti?.additionalData?.image || FALLBACK_AVATAR} alt="" />

        <Body $expanded={expanded}>
          <Text>{text}</Text>
        </Body>

        <Actions $expanded={expanded}>
          <ViewAll
            type="button"
            onClick={handleViewAll}
            onKeyDown={e => e.stopPropagation()}
            tabIndex={expanded ? 0 : -1}
            aria-label={unreadCount > 0 ? `View all ${unreadCount} notifications` : "View all notifications"}
          >
            ALL{unreadCount > 0 ? ` ${badge}` : ""}
          </ViewAll>
          <Dismiss
            type="button"
            onClick={handleDismiss}
            onKeyDown={e => e.stopPropagation()}
            tabIndex={expanded ? 0 : -1}
            aria-label="Dismiss notification"
          >
            ✕
          </Dismiss>
        </Actions>
      </Toast>
    </>
  );
};

export default NotificationPopup;
